/**
 * The published contract, read at run time.
 *
 * `docs/plan/contracts/fixtures/` is the survey line. The Fleet API asserts it satisfies
 * these files; `nexus-deploy` asserts it satisfies the same ones; this console asserts it
 * consumes them (README 4.2). So the mock below is not "made-up data that looks about
 * right", it is the recorded contract, and a fixture regenerated upstream changes what this
 * console renders without anybody editing a screen.
 *
 * `import.meta.glob` is Vite's and it is compile time: the bundler rewrites it, so the
 * fixtures are inlined into the dev server, the unit run and the contract run alike, with no
 * fetch and no server. Outside Vite it is undefined, which is why the accessor throws with
 * the reason named rather than returning an empty object that would read as an empty fleet.
 */

type Json = Record<string, unknown>

interface GlobbedModule {
  default: Json
}

function load(pattern: Record<string, unknown>): Record<string, Json> {
  const out: Record<string, Json> = {}
  for (const [path, mod] of Object.entries(pattern)) {
    const name = path.split('/').pop()
    if (!name) continue
    out[name] = (mod as GlobbedModule).default ?? (mod as Json)
  }
  return out
}

const FIXTURES = load(import.meta.glob('/docs/plan/contracts/fixtures/*.json', { eager: true }))

/**
 * console-api's fixtures, a SIBLING directory and deliberately not nested under the fleet ones.
 *
 * The harness guards enumerate the fleet fixtures non-recursively, so nesting these there would
 * make them invisible today and sweep them into fleet-schema validation the day one character
 * becomes `rglob` - at which point every console fixture is asserted against schemas it was
 * never written to satisfy. A sibling cannot be captured by that edit. (fleet-api-9c's reasoning.)
 */
// The deck-* fixtures are left out: the Decks screens and their mock never read them through
// `consoleFixture` (tests/contract/decks.test.ts reads them from disk), and every eager file is one
// more request when the dev server loads the app. Twenty of them took the e2e rail walk past what a
// Chromium page can fetch ahead of a tab, so a late screen rendered blank (ERR_INSUFFICIENT_RESOURCES).
const CONSOLE_FIXTURES = load(
  import.meta.glob(['/docs/plan/contracts/console/fixtures/*.json', '!/docs/plan/contracts/console/fixtures/deck-*.json'], {
    eager: true,
  }),
)
const SCHEMAS = load(import.meta.glob('/docs/plan/contracts/schemas/index.json', { eager: true }))

/** One published fixture, by file name. Throws loudly: a missing fixture is not an empty one. */
export function fixture<T = Json>(name: string): T {
  const doc = FIXTURES[name]
  if (!doc) {
    throw new Error(
      `no fixture "${name}" in docs/plan/contracts/fixtures. ` +
        `Present: ${Object.keys(FIXTURES).sort().join(', ') || '(none - is docs/plan unzipped in this root?)'}`,
    )
  }
  return doc as T
}

/** The response half of an endpoint fixture, which is what a consumer sees. */
export function fixtureResponse<T = Json>(name: string): T {
  const doc = fixture<{ response?: { body?: unknown } }>(name)
  if (!doc.response?.body) throw new Error(`fixture "${name}" carries no response body`)
  return doc.response.body as T
}

/** The request half. The relay sends these; the console renders what they landed as. */
export function fixtureRequest<T = Json>(name: string): T {
  const doc = fixture<{ request?: { body?: unknown } }>(name)
  if (!doc.request?.body) throw new Error(`fixture "${name}" carries no request body`)
  return doc.request.body as T
}

/**
 * The response half of a console-api fixture, which is what a console consumes.
 *
 * Separate accessor rather than a flag on `fixture()`, because the two directories are two
 * different contracts: one describes the deployment-facing Fleet API, the other describes
 * console-api. A single lookup across both would let a missing file in one resolve to a
 * same-named file in the other, which is a silent wrong answer rather than a loud absence.
 */
export function consoleFixture<T = Json>(name: string): T {
  const doc = CONSOLE_FIXTURES[name] as { response?: { body?: unknown } } | undefined
  if (!doc?.response?.body) {
    throw new Error(
      `no console fixture "${name}" in docs/plan/contracts/console/fixtures. ` +
        `Present: ${Object.keys(CONSOLE_FIXTURES).sort().join(', ') || '(none - has plan-sync run?)'}`,
    )
  }
  return doc.response.body as T
}

interface SchemaIndex {
  schemaPackageVersion: string
  definitionKinds: Record<string, { schema: string; version: string; phase: number }>
  messageTypes: Record<string, { schema: string; family: string }>
}

function schemaIndex(): SchemaIndex {
  const doc = SCHEMAS['index.json']
  if (!doc) throw new Error('docs/plan/contracts/schemas/index.json is not readable from here')
  return doc as unknown as SchemaIndex
}

/**
 * The definition kinds, DERIVED from the schema package registry rather than written down.
 *
 * D5 says sixteen and the plan has said thirteen and fifteen on the way there. A console that
 * hard-codes the list is a console that renders fifteen tiles the morning the seventeenth
 * lands, so the list comes from the registry the relay dispatches through.
 */
export function definitionKinds(): string[] {
  return Object.keys(schemaIndex().definitionKinds)
}

/** The upward message types, derived the same way. Fifteen today (D4, D30, D31). */
export function messageTypes(): string[] {
  return Object.keys(schemaIndex().messageTypes)
}
